/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class Base64Service {
  private PADCHAR = '=';
  private ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  /* eslint-disable */
  private base64abc = [
    'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U',
    'V', 'W', 'X', 'Y', 'Z', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p',
    'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '+',
    '/'
  ];
  /* eslint-enable */

  private getByte64(s, i) {
    const idx = this.ALPHA.indexOf(s.charAt(i));
    if (idx === -1) {
      throw new Error('Cannot decode base64');
    }
    return idx;
  }

  private getByte(s, i) {
    const x = s.charCodeAt(i);
    if (x > 255) {
      throw new Error('INVALID_CHARACTER_ERR: DOM Exception 5');
    }
    return x;
  }

  bytesToBase64(bytes) {
    let result = '',
      i;
    const l = bytes.length;
    for (i = 2; i < l; i += 3) {
      result += this.base64abc[bytes[i - 2] >> 2];
      result += this.base64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)];
      result += this.base64abc[((bytes[i - 1] & 0x0f) << 2) | (bytes[i] >> 6)];
      result += this.base64abc[bytes[i] & 0x3f];
    }
    if (i === l + 1) {
      // 1 octet yet to write
      result += this.base64abc[bytes[i - 2] >> 2];
      result += this.base64abc[(bytes[i - 2] & 0x03) << 4];
      result += '==';
    }
    if (i === l) {
      // 2 octets yet to write
      result += this.base64abc[bytes[i - 2] >> 2];
      result += this.base64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)];
      result += this.base64abc[(bytes[i - 1] & 0x0f) << 2];
      result += '=';
    }
    return result;
  }

  decode(s) {
    // convert to string
    s = '' + s;
    let pads;
    let i;
    let b10;
    let iMax = s.length;
    if (iMax === 0) {
      return s;
    }

    if (iMax % 4 !== 0) {
      throw new Error('Cannot decode base64');
    }

    pads = 0;
    if (s.charAt(iMax - 1) === this.PADCHAR) {
      pads = 1;
      if (s.charAt(iMax - 2) === this.PADCHAR) {
        pads = 2;
      }
      iMax -= 4;
    }

    const x = [];
    for (i = 0; i < iMax; i += 4) {
      b10 =
        (this.getByte64(s, i) << 18) |
        (this.getByte64(s, i + 1) << 12) |
        (this.getByte64(s, i + 2) << 6) |
        this.getByte64(s, i + 3);
      x.push(String.fromCharCode(b10 >> 16, (b10 >> 8) & 0xff, b10 & 0xff));
    }

    switch (pads) {
      case 1:
        b10 = (this.getByte64(s, i) << 18) | (this.getByte64(s, i + 1) << 12) | (this.getByte64(s, i + 2) << 6);
        x.push(String.fromCharCode(b10 >> 16, (b10 >> 8) & 0xff));
        break;
      case 2:
        b10 = (this.getByte64(s, i) << 18) | (this.getByte64(s, i + 1) << 12);
        x.push(String.fromCharCode(b10 >> 16));
        break;
    }
    return x.join('');
  }

  encode(s) {
    if (arguments.length !== 1) {
      throw new Error('SyntaxError: Not enough arguments');
    }
    let i;
    let b10;
    const x = [];
    // convert to string
    s = '' + s;
    const iMax = s.length - (s.length % 3);
    if (s.length === 0) {
      return s;
    }
    for (i = 0; i < iMax; i += 3) {
      b10 = (this.getByte(s, i) << 16) | (this.getByte(s, i + 1) << 8) | this.getByte(s, i + 2);
      x.push(this.ALPHA.charAt(b10 >> 18));
      x.push(this.ALPHA.charAt((b10 >> 12) & 0x3f));
      x.push(this.ALPHA.charAt((b10 >> 6) & 0x3f));
      x.push(this.ALPHA.charAt(b10 & 0x3f));
    }
    switch (s.length - iMax) {
      case 1:
        b10 = this.getByte(s, i) << 16;
        x.push(this.ALPHA.charAt(b10 >> 18) + this.ALPHA.charAt((b10 >> 12) & 0x3f) + this.PADCHAR + this.PADCHAR);
        break;
      case 2:
        b10 = (this.getByte(s, i) << 16) | (this.getByte(s, i + 1) << 8);
        x.push(
          this.ALPHA.charAt(b10 >> 18) +
            this.ALPHA.charAt((b10 >> 12) & 0x3f) +
            this.ALPHA.charAt((b10 >> 6) & 0x3f) +
            this.PADCHAR
        );
        break;
    }
    return x.join('');
  }

  private stringToUTF8ByteArray(str: string) {
    const encoder = new TextEncoder();
    return encoder.encode(str);
  }

  paramToBase64(param: any) {
    if (typeof param !== 'string') {
      param = JSON.stringify(param);
    }
    return this.bytesToBase64(this.stringToUTF8ByteArray(param));
  }
}
